import { Controller } from "@hotwired/stimulus"
import DailyIframe from "@daily-co/daily-js"
import Sortable from "sortablejs"

// ConsLog Meetings branded join page (/meet/:token). Embeds the Daily
// Prebuilt call (bundled — no CDN fetch at page time; staging/prod networks
// can't break it) themed with the project's brand color (owner blue /
// builder orange). On leave, swaps to a ConsLog "you left" panel with a
// Rejoin button; an expired/closed room shows the calm "meeting has ended"
// message instead of an error.
const AGENDA_CLIP_CHIP = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-3.5 h-3.5 shrink-0" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"/></svg>'

export default class extends Controller {
  static targets = ["frame", "loading", "left", "transcriptionPill", "transcriptionLabel", "transcriptionDot",
                    "agendaPanel", "agendaButton", "agendaList", "agendaInput",
                    "agendaAddBtn", "agendaError", "agendaCount", "agendaSort",
                    "agendaFile", "agendaGrab"]
  static values = {
    dailyUrl: String, accent: String, accentDark: String, over: Boolean, userName: String, avatar: String,
    // Item 22: creator-only mid-meeting transcription toggle.
    creator: Boolean, paused: Boolean, toggleUrl: String,
    // Creator-only: add / edit / delete agenda items from inside the call.
    addAgendaUrl: String, updateAgendaUrl: String, deleteAgendaUrl: String,
    reorderAgendaUrl: String,
    // Paperclip on an agenda line, mid-call (Larry 2026-09-17).
    agendaPresignUrl: String, agendaRegisterUrl: String, agendaRemoveUploadUrl: String,
    // Agenda panel width, remembered per person per project.
    panelWidth: Number, panelKey: String, panelSaveUrl: String
  }

  connect() {
    // The agenda column works whether or not the call itself joins, so it's
    // restored before the early return below.
    this._restoreAgendaState()
    this._applyPanelWidth()
    if (this.overValue || !this.dailyUrlValue || !this.hasFrameTarget) return
    this._join()
  }

  // ── Agenda checklist (Phelipe 2026-09-01) ───────────────────────────
  // Ticking an item off is the chair's own running checklist — kept in this
  // browser, per meeting, so a refresh mid-call doesn't lose the place. It
  // deliberately does NOT change the agenda for other participants.
  _agendaKey() {
    return `conslog-agenda-ticks:${window.location.pathname}`
  }

  _agendaTicks() {
    if (!this._ticks) {
      try { this._ticks = new Set(JSON.parse(localStorage.getItem(this._agendaKey()) || "[]")) } catch (_) { this._ticks = new Set() }
    }
    return this._ticks
  }

  _saveAgendaTicks() {
    try { localStorage.setItem(this._agendaKey(), JSON.stringify([...this._agendaTicks()])) } catch (_) {}
  }

  _restoreAgendaState() {
    if (!this.hasAgendaListTarget) return

    // Remember the authored order once, so "Agenda order" can always return.
    this._agendaOrder = [...this.agendaListTarget.children]
    this._initAgendaSortable()
    this._refreshAgendaArrows()

    const ticks = this._agendaTicks()
    this.agendaListTarget.querySelectorAll("[data-agenda-line]").forEach(li => {
      const box = li.querySelector("[data-agenda-check]")
      if (!box) return
      box.checked = ticks.has(li.dataset.agendaKey)
      this._paintAgendaLine(li, box.checked)
    })

    if (this.hasAgendaSortTarget) {
      let saved = null
      try { saved = localStorage.getItem(`${this._agendaKey()}:sort`) } catch (_) {}
      if (saved) this.agendaSortTarget.value = saved
      this._applyAgendaSort()
    }
  }

  agendaChecked(event) {
    const li = event.currentTarget.closest("[data-agenda-line]")
    if (!li) return

    const on = event.currentTarget.checked
    on ? this._agendaTicks().add(li.dataset.agendaKey) : this._agendaTicks().delete(li.dataset.agendaKey)
    this._saveAgendaTicks()
    this._paintAgendaLine(li, on)
    this._applyAgendaSort()
  }

  _paintAgendaLine(li, done) {
    const subject = li.querySelector("[data-agenda-subject]") || li.querySelector("span.min-w-0")
    if (subject) subject.classList.toggle("line-through", done)
    li.classList.toggle("opacity-55", done)
  }

  agendaSortChanged() {
    try { localStorage.setItem(`${this._agendaKey()}:sort`, this.agendaSortTarget.value) } catch (_) {}
    this._applyAgendaSort()
  }

  // "Agenda order" restores exactly what was authored, bands and all. The
  // other two flatten into a working list — a band between reordered items
  // would be lying about where they came from, so bands hide there.
  _applyAgendaSort() {
    if (!this.hasAgendaListTarget || !this._agendaOrder) return

    const mode = this.hasAgendaSortTarget ? this.agendaSortTarget.value : "agenda"
    const list = this.agendaListTarget
    // Dragging only makes sense against the real agenda order — in the
    // "discussed first" views the list is a computed working copy.
    if (this._agendaSortable) this._agendaSortable.option("disabled", mode !== "agenda")

    if (mode === "agenda") {
      this._agendaOrder.forEach(el => list.appendChild(el))
      list.querySelectorAll("[data-agenda-band]:not([data-agenda-band-hidden])").forEach(el => el.classList.remove("hidden"))
      return
    }

    list.querySelectorAll("[data-agenda-band]").forEach(el => el.classList.add("hidden"))
    const lines = this._agendaOrder.filter(el => el.matches?.("[data-agenda-line]"))
    const done = el => el.querySelector("[data-agenda-check]")?.checked === true
    const wanted = mode === "discussed"
      ? [...lines.filter(done), ...lines.filter(el => !done(el))]
      : [...lines.filter(el => !done(el)), ...lines.filter(done)]
    wanted.forEach(el => list.appendChild(el))
  }

  disconnect() {
    if (this._call) {
      try { this._call.destroy() } catch { /* already gone */ }
      this._call = null
    }
  }

  rejoin() {
    if (this.hasLeftTarget) { this.leftTarget.classList.add("hidden"); this.leftTarget.classList.remove("flex") }
    this._join()
  }

  // Agenda alongside the call (Larry 2026-08-21) — he started a meeting and
  // had nowhere to see what he'd planned to cover. Slides a column in beside
  // the video instead of sending him back to the meeting page.
  toggleAgenda() {
    if (!this.hasAgendaPanelTarget) return
    const open = this.agendaPanelTarget.classList.toggle("hidden")
    if (this.hasAgendaButtonTarget) {
      this.agendaButtonTarget.setAttribute("aria-expanded", open ? "false" : "true")
      this.agendaButtonTarget.classList.toggle("bg-gray-100", !open)
    }
  }

  // ── Drag to reorder mid-call (Larry 2026-09-01) ────────────────────────
  // The creator drags a line into the order it's actually being discussed
  // and the order saves on the meeting, so everyone sees it (Phelipe's
  // call: organizer drags, all see). Top-level lines carry their sub-items;
  // dividers can't be dragged but lines can move past them into another
  // section; nothing ever drops into the middle of another line's
  // sub-items. The DB-backed items and the typed agenda stay two blocks.
  _initAgendaSortable() {
    if (this._agendaSortable || !this.reorderAgendaUrlValue || !this.hasAgendaListTarget) return

    const list = this.agendaListTarget
    const isItem = el => el?.hasAttribute?.("data-agenda-item")

    this._agendaSortable = Sortable.create(list, {
      animation: 150,
      draggable: "[data-agenda-line]",
      // Only the small grip starts a drag (Phelipe 2026-09-01), so the
      // checkbox, links, and text stay plainly clickable.
      handle: "[data-agenda-grip]",
      filter: "[data-agenda-band]",
      preventOnFilter: false,
      // While dragging, the row's place in the list is drawn as an empty
      // dashed slot that follows the cursor — you see exactly where it
      // lands before letting go (Phelipe 2026-09-01).
      ghostClass: "agenda-drop-slot",
      // Dragging never changes depth (Phelipe 2026-09-01): a top-level line
      // moves among top-level lines, a sub-item moves among sub-item slots
      // (its own group or another parent's) — the ‹ › arrows change depth.
      // The DB-backed items block always stays above the typed lines.
      onMove: e => {
        const D = e.dragged, R = e.related, after = e.willInsertAfter
        if (isItem(D) !== isItem(R) && !R.hasAttribute("data-agenda-band")) return false
        if (!isItem(D) && isItem(R)) return false
        const dL = this._agendaLevel(D)
        if (dL === 0) return !(this._agendaLevel(R) > 0)
        // A sub-item needs a typed line right above it to hang from, at
        // most one level shallower than itself.
        const prev = after ? R : R.previousElementSibling
        const pL = this._agendaLevel(prev)
        return pL != null && !isItem(prev) && pL >= dL - 1
      },
      onStart: e => {
        this._dragChildren = this._agendaChildrenOf(e.item)
        // Sub-items travel with their parent on drop; fade them meanwhile so
        // the slot is the only thing that reads as "here".
        this._dragChildren.forEach(child => child.classList.add("opacity-30"))
      },
      onEnd: e => {
        let anchor = e.item
        ;(this._dragChildren || []).forEach(child => { child.classList.remove("opacity-30"); anchor.after(child); anchor = child })
        this._dragChildren = null
        this._agendaOrder = [...list.children]
        this._renumberAgenda()
        this._refreshAgendaArrows()
        this._persistAgendaOrder()
      }
    })
  }

  // Everything nested under a line: the following lines that sit deeper
  // than it, up to the next line at its level or shallower.
  _agendaChildrenOf(li) {
    const out = []
    const lvl = this._agendaLevel(li)
    let el = li.nextElementSibling
    while (el && this._agendaLevel(el) != null && this._agendaLevel(el) > lvl) { out.push(el); el = el.nextElementSibling }
    return out
  }

  // Depth of a panel row: DB items and top-level lines are 0, sub-items
  // carry their level; dividers and anything else are null.
  _agendaLevel(el) {
    if (!el?.hasAttribute?.("data-agenda-line")) return null
    // Saved items carry their own depth since 2026-09-17 (meeting_items
    // .agenda_level); before that they were pinned to 0 here.
    return Number(el.dataset.level || 0)
  }

  // ‹ › on a typed line (Phelipe 2026-09-01): › tucks it under the line
  // above, ‹ pulls it out one level — the only way a line changes depth.
  // Whatever is nested under it moves the same amount.
  outdentAgendaLine(event) {
    const li = event.currentTarget.closest("[data-agenda-line]")
    const lvl = this._agendaLevel(li)
    if (!li || !lvl) return
    this._shiftAgendaDepth(li, -1)
  }

  indentAgendaLine(event) {
    const li = event.currentTarget.closest("[data-agenda-line]")
    if (!li || !this._canIndentAgendaLine(li)) return
    this._shiftAgendaDepth(li, +1)
  }

  _canIndentAgendaLine(li) {
    const prev = li.previousElementSibling
    const pL = this._agendaLevel(prev)
    if (pL == null) return false
    const lvl = this._agendaLevel(li)
    return lvl < 5 && lvl <= pL
  }

  _shiftAgendaDepth(li, delta) {
    const rows = [li, ...this._agendaChildrenOf(li)]
    rows.forEach(row => this._setAgendaLevel(row, this._agendaLevel(row) + delta))
    if (this.hasAgendaListTarget) this._agendaOrder = [...this.agendaListTarget.children]
    this._renumberAgenda()
    this._refreshAgendaArrows()
    this._persistAgendaOrder()
  }

  // Dim an arrow that can't apply: ‹ on a top-level line, › on a line with
  // nothing above to nest under.
  _refreshAgendaArrows() {
    if (!this.hasAgendaListTarget) return
    this.agendaListTarget.querySelectorAll("[data-agenda-line]").forEach(li => {
      const out = li.querySelector("[data-agenda-outdent]")
      const inn = li.querySelector("[data-agenda-indent]")
      if (out) out.classList.toggle("invisible", this._agendaLevel(li) === 0)
      if (inn) inn.classList.toggle("invisible", !this._canIndentAgendaLine(li))
    })
  }

  // Re-draw a typed line at a new depth: indent, and the marker in front —
  // a running number for top-level lines, a dot for sub-items (mirrors the
  // server-rendered row in meet_joins/show).
  _setAgendaLevel(li, level) {
    const lvl = Math.max(0, Math.min(5, level))
    li.dataset.level = String(lvl)
    const row = li.querySelector(":scope > div")
    if (row) row.style.paddingLeft = `${lvl * 0.9}rem`
    const text = li.querySelector("span.min-w-0, [data-agenda-body]")
    const marker = li.querySelector("[data-agenda-num], span.rounded-full.w-1")
    if (lvl === 0) {
      if (marker && !marker.hasAttribute("data-agenda-num")) {
        const num = document.createElement("span")
        num.className = "text-[13px] text-gray-400 tabular-nums shrink-0"
        num.setAttribute("data-agenda-num", "")
        marker.replaceWith(num)
      }
      text?.classList.add("font-medium"); text?.classList.remove("text-[#c8cdd6]")
    } else {
      if (marker && marker.hasAttribute("data-agenda-num")) {
        const dot = document.createElement("span")
        dot.className = "mt-[7px] rounded-full shrink-0 w-1 h-1 bg-gray-300"
        marker.replaceWith(dot)
      }
      text?.classList.remove("font-medium"); text?.classList.add("text-[#c8cdd6]")
    }
  }

  _persistAgendaOrder() {
    const list = this.agendaListTarget
    const items = [...list.querySelectorAll("[data-agenda-item]")]
      .map(li => ({ id: li.dataset.agendaItem, level: Number(li.dataset.level || 0) }))
    const lines = [...list.children]
      .filter(el => el.hasAttribute("data-agenda-band") || (el.hasAttribute("data-agenda-line") && !el.hasAttribute("data-agenda-item")))
      .map(el => el.hasAttribute("data-agenda-band")
        ? { text: el.textContent.trim(), band: true }
        : { text: el.dataset.agendaKey, level: Number(el.dataset.level || 0) })

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.reorderAgendaUrlValue, {
      method: "PATCH",
      headers: { "X-CSRF-Token": token || "", "Accept": "application/json", "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ items, lines })
    })
      .then(r => r.json().then(d => (r.ok ? d : Promise.reject(d))))
      .then(() => { try { this._call?.sendAppMessage({ type: "agenda_reordered" }, "*") } catch (_) { /* no call yet */ } })
      .catch(d => this._agendaFail(d?.error || "Couldn't save the new order — try again."))
  }

  // Another attendee's panel: the organizer just reordered, so pull the
  // panel fresh from the server and re-apply this viewer's own ticks.
  _refreshAgendaPanel() {
    if (!this.hasAgendaListTarget) return
    fetch(window.location.href, { credentials: "same-origin", headers: { "Accept": "text/html" } })
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, "text/html")
        const fresh = doc.querySelector('[data-meet-join-target="agendaList"]')
        if (!fresh) return
        this.agendaListTarget.innerHTML = fresh.innerHTML
        this._agendaOrder = null
        this._restoreAgendaState()
      })
      .catch(() => { /* next reorder or a reload catches up */ })
  }

  // Enter adds without reaching for the button — the creator is usually
  // typing one-handed while the meeting carries on.
  agendaKeydown(e) {
    if (e.key === "Enter") { e.preventDefault(); this.addAgendaItem() }
  }

  // Creator adds an agenda line mid-call (Phelipe 2026-08-21). It saves onto
  // the meeting as a normal item, so the minutes pick it up afterwards.
  addAgendaItem() {
    if (!this.hasAgendaInputTarget || !this.addAgendaUrlValue || this._agendaBusy) return

    const subject = this.agendaInputTarget.value.trim()
    if (!subject) return this._agendaFail("Type the item first.")

    this._agendaBusy = true
    if (this.hasAgendaAddBtnTarget) this.agendaAddBtnTarget.disabled = true
    this._agendaFail("")

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.addAgendaUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token || "", "Accept": "application/json",
                 "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ subject })
    })
      .then(r => r.json().then(d => (r.ok ? d : Promise.reject(d))))
      .then(item => {
        this._appendAgendaItem(item.subject, item.id)
        this.agendaInputTarget.value = ""
        this.agendaInputTarget.focus()
      })
      .catch(d => this._agendaFail(d?.error || "Couldn't add that — try again."))
      .finally(() => {
        this._agendaBusy = false
        if (this.hasAgendaAddBtnTarget) this.agendaAddBtnTarget.disabled = false
      })
  }

  // Mirrors the server-rendered row, controls included — a line you just
  // added is editable straight away, without a refresh.
  _appendAgendaItem(subject, id) {
    if (!this.hasAgendaListTarget) return
    const li = document.createElement("li")
    li.className = "relative px-4 py-2.5 group"
    // The row must carry the same hooks the server-rendered rows do, or the
    // drag code cannot see it, the tick state cannot find it and the saved
    // order leaves it out (Larry 2026-09-15: a line added during the meeting
    // could not be moved and looked different from the rest).
    li.setAttribute("data-agenda-line", "")
    li.dataset.agendaKey = subject
    li.dataset.level = "0"
    if (id) { li.dataset.agendaItem = String(id); li.dataset.agendaItemId = String(id) }

    // Only the organizer can add a line mid-call, and on the server only
    // their own lines get the grip — so the grip rides with the id.
    const grip = document.createElement("span")
    grip.setAttribute("data-agenda-grip", "")
    grip.title = "Drag to reorder"
    grip.className = "mt-0.5 shrink-0 -ml-1 text-gray-400 hover:text-white cursor-grab active:cursor-grabbing select-none"
    grip.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-3.5 h-3.5" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/></svg>'

    const box = document.createElement("input")
    box.type = "checkbox"
    box.setAttribute("data-agenda-check", "")
    box.setAttribute("data-action", "change->meet-join#agendaChecked")
    box.className = "mt-0.5 shrink-0 rounded bg-[#2c3542] border-[#3a4454] text-brand focus:ring-brand cursor-pointer"

    const num = document.createElement("span")
    num.className = "text-[13px] text-gray-400 tabular-nums shrink-0"
    num.setAttribute("data-agenda-num", "")
    num.textContent = `${this.agendaListTarget.querySelectorAll("li").length + 1}.`

    const text = document.createElement("div")
    // The room is dark (2026-09-17): this must match the server-rendered
    // rows' #e5e8ee, or a line added during the meeting is invisible
    // (Larry's screenshot, 2026-09-17 — items 2 and 3 looked empty).
    text.className = "text-[13px] text-[#e5e8ee]"
    text.setAttribute("data-agenda-subject", "")
    text.textContent = subject

    const col = document.createElement("div")
    col.className = "min-w-0 flex-1"
    col.setAttribute("data-agenda-body", "")
    col.appendChild(text)

    const wrap = document.createElement("div")
    wrap.className = "flex gap-2"
    if (id) wrap.append(grip)
    wrap.append(box, num, col)

    if (id) wrap.appendChild(this._agendaRowActions())
    li.appendChild(wrap)
    this.agendaListTarget.appendChild(li)
    // Join the authored order so "Agenda order" can return to it, take a
    // number, and become draggable without a refresh.
    this._agendaOrder = [...this.agendaListTarget.children]
    this._renumberAgenda()
    this._refreshAgendaArrows()
    this._renderAgendaCount()
  }

  // ── Paperclip on an agenda line, mid-call (Larry 2026-09-17) ──────────
  // The chips were already drawn here; there was no way to ADD one without
  // leaving the room. Same three steps the meeting page uses: presign → PUT
  // to S3 → register against the line's own text, which is the key the
  // attachment map is stored under.

  pickAgendaFile(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasAgendaFileTarget) return
    this._clipRow = event.currentTarget.closest("[data-agenda-line]")
    if (!this._clipRow) return
    this.agendaFileTarget.value = ""
    this.agendaFileTarget.click()
  }

  async agendaFilesPicked() {
    const li = this._clipRow
    const files = [...(this.agendaFileTarget.files || [])]
    if (!li || !files.length) return
    const key = li.dataset.agendaKey
    if (!key) return this._agendaFail("Name the agenda item first, then attach a file.")
    if (!this.agendaPresignUrlValue || !this.agendaRegisterUrlValue) return

    const clip = li.querySelector("[data-agenda-clip]")
    clip?.classList.add("animate-pulse")
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const headers = { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token || "" }
      const presign = await fetch(this.agendaPresignUrlValue, {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ files: files.map(f => ({ filename: f.name, content_type: f.type || "application/octet-stream", byte_size: f.size })) })
      })
      const presigned = await presign.json()
      if (!presign.ok || !presigned.uploads) throw new Error(presigned.error || "Couldn't attach the file.")
      await Promise.all(presigned.uploads.map((u, i) =>
        fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: files[i] })
          .then(r => { if (!r.ok) throw new Error("Couldn't attach the file.") })
      ))
      const reg = await fetch(this.agendaRegisterUrlValue, {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ line_key: key, files: presigned.uploads.map(u => ({ cache_name: u.cache_name, filename: u.filename })) })
      })
      const data = await reg.json()
      if (!reg.ok || !data.ok) throw new Error(data.error || "Couldn't attach the file.")
      this._renderAgendaChips(li, (data.attachments || {})[key])
      try { this._call?.sendAppMessage({ type: "agenda_reordered" }, "*") } catch (_) { /* no call yet */ }
    } catch (e) {
      this._agendaFail(e.message || "Couldn't attach the file.")
    } finally {
      clip?.classList.remove("animate-pulse")
      this.agendaFileTarget.value = ""
    }
  }

  // Chips sit inside the line's own text so they wrap with it — the same
  // markup the server renders, redrawn from the map register returns.
  _renderAgendaChips(li, list) {
    const host = li.querySelector("[data-agenda-subject]") || li.querySelector("span.min-w-0")
    if (!host) return
    host.querySelectorAll("[data-agenda-chip]").forEach(el => el.remove())
    ;(list || []).forEach(att => {
      const a = document.createElement("a")
      a.setAttribute("data-agenda-chip", "")
      a.href = att.url
      a.target = "_blank"
      a.rel = "noopener"
      a.className = "inline-flex items-center gap-1 align-middle ml-1.5 px-1.5 py-px rounded bg-[#2c3542] hover:bg-[#3a4454] text-[13px] text-[#c8cdd6] max-w-[170px]"
      a.innerHTML = AGENDA_CLIP_CHIP
      const name = document.createElement("span")
      name.className = "truncate"
      name.textContent = att.filename || "File"
      a.appendChild(name)
      host.appendChild(a)
    })
  }

  // ── Drag the agenda panel wider (Larry 2026-09-17) ────────────────────
  // Saved per person AND per project. The width is applied as an inline
  // style, never a class, so the phone keeps its full-width panel.

  _applyPanelWidth() {
    if (!this.hasAgendaPanelTarget || window.innerWidth < 640) return
    const px = Number(this.panelWidthValue || 0)
    if (px > 0) this.agendaPanelTarget.style.width = `${this._clampPanel(px)}px`
  }

  _clampPanel(px) {
    const max = Math.min(720, Math.round(window.innerWidth * 0.7))
    return Math.max(240, Math.min(max, Math.round(px)))
  }

  panelResizeStart(event) {
    if (!this.hasAgendaPanelTarget || window.innerWidth < 640) return
    event.preventDefault()
    const startX = event.clientX
    const startW = this.agendaPanelTarget.getBoundingClientRect().width
    const move = e => {
      // The handle is on the LEFT edge, so dragging left widens the panel.
      this.agendaPanelTarget.style.width = `${this._clampPanel(startW + (startX - e.clientX))}px`
    }
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      document.body.style.userSelect = ""
      this._savePanelWidth(this.agendaPanelTarget.getBoundingClientRect().width)
    }
    document.body.style.userSelect = "none"
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  _savePanelWidth(px) {
    const width = this._clampPanel(px)
    this.panelWidthValue = width
    if (!this.panelSaveUrlValue || !this.panelKeyValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.panelSaveUrlValue, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token || "" },
      credentials: "same-origin",
      body: JSON.stringify({ setting: { agenda_panel_width_by_project: { [this.panelKeyValue]: width } } })
    }).catch(() => { /* a lost width isn't worth a message mid-call */ })
  }

  _agendaRowActions() {
    const PENCIL = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>'
    const TRASH = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>'
    const CHEV_L = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-3.5 h-3.5" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>'
    const CHEV_R = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-3.5 h-3.5" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>'
    const CLIP = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"/></svg>'
    const span = document.createElement("span")
    span.className = "shrink-0 flex items-start gap-0.5"
    span.setAttribute("data-agenda-actions", "")
    const mk = (svg, label, cls, action) => {
      const b = document.createElement("button")
      b.type = "button"
      b.setAttribute("aria-label", label)
      b.setAttribute("data-action", `click->meet-join#${action}`)
      b.className = `p-0.5 cursor-pointer ${cls}`
      b.innerHTML = svg
      return b
    }
    const out = mk(CHEV_L, "Move out to parent level", "text-blue-400 hover:text-blue-300 invisible", "outdentAgendaLine")
    out.setAttribute("data-agenda-outdent", "")
    const inn = mk(CHEV_R, "Make a sub-item of the line above", "text-blue-400 hover:text-blue-300", "indentAgendaLine")
    inn.setAttribute("data-agenda-indent", "")
    const clip = mk(CLIP, "Attach a file to this item", "text-gray-400 hover:text-white", "pickAgendaFile")
    clip.setAttribute("data-agenda-clip", "")
    span.append(
      out, inn, clip,
      mk(PENCIL, "Edit", "text-gray-400 hover:text-white", "editAgendaItem"),
      mk(TRASH, "Delete", "text-gray-400 hover:text-red-400", "confirmDeleteAgendaItem")
    )
    return span
  }

  // ── Edit / delete your own agenda lines (Phelipe 2026-08-21) ──────────
  // Only lines this person added carry the controls; the server enforces
  // the same rule.

  editAgendaItem(e) {
    const li = e.currentTarget.closest("li[data-agenda-item-id]")
    if (!li || li.querySelector("input[data-agenda-edit]")) return

    const subjectEl = li.querySelector("[data-agenda-subject]")
    const actions = li.querySelector("[data-agenda-actions]")
    const original = subjectEl.textContent

    const input = document.createElement("input")
    input.type = "text"
    input.value = original
    input.setAttribute("data-agenda-edit", "")
    input.className = "w-full px-2 py-1 text-[13px] bg-[#2c3542] text-[#e5e8ee] border border-[#3a4454] rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"

    const finish = (save) => {
      const next = input.value.trim()
      if (!save || !next || next === original) {
        input.replaceWith(subjectEl)
        if (actions) actions.classList.remove("hidden")
        return
      }
      this._agendaRequest(this.updateAgendaUrlValue, "PATCH",
        { item_id: li.dataset.agendaItemId, subject: next })
        .then(() => {
          subjectEl.textContent = next
          input.replaceWith(subjectEl)
          if (actions) actions.classList.remove("hidden")
        })
        .catch(msg => {
          this._agendaFail(msg)
          input.replaceWith(subjectEl)
          if (actions) actions.classList.remove("hidden")
        })
    }

    input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") { ev.preventDefault(); finish(true) }
      if (ev.key === "Escape") { ev.preventDefault(); finish(false) }
    })
    input.addEventListener("blur", () => finish(true))

    if (actions) actions.classList.add("hidden")
    subjectEl.replaceWith(input)
    input.focus()
    input.select()
  }

  // Inline confirm row rather than a browser confirm() — same rule the rest
  // of the app follows, and a modal would be heavy in a narrow panel.
  confirmDeleteAgendaItem(e) {
    const li = e.currentTarget.closest("li[data-agenda-item-id]")
    if (!li || li.querySelector("[data-agenda-confirm]")) return

    const actions = li.querySelector("[data-agenda-actions]")
    const bar = document.createElement("div")
    bar.setAttribute("data-agenda-confirm", "")
    bar.className = "flex items-center justify-end gap-2 mt-1.5"

    const mk = (label, cls) => {
      const b = document.createElement("button")
      b.type = "button"
      b.textContent = label
      b.className = `px-2 py-0.5 text-[13px] rounded-lg cursor-pointer ${cls}`
      return b
    }
    const cancel = mk("Cancel", "text-gray-500 hover:bg-gray-100")
    const del = mk("Delete", "text-white bg-red-600 hover:bg-red-700")

    const close = () => { bar.remove(); if (actions) actions.classList.remove("hidden") }
    cancel.addEventListener("click", close)
    del.addEventListener("click", () => {
      del.disabled = true
      this._agendaRequest(this.deleteAgendaUrlValue, "DELETE",
        { item_id: li.dataset.agendaItemId })
        .then(() => { li.remove(); this._renumberAgenda(); this._renderAgendaCount() })
        .catch(msg => { this._agendaFail(msg); close() })
    })

    bar.append(cancel, del)
    if (actions) actions.classList.add("hidden")
    li.appendChild(bar)
  }

  // Only numbered rows count — sub-items and dividers sit between them, so
  // counting every <li> left gaps (5, 6, 11) after a reorder or delete.
  _renumberAgenda() {
    if (!this.hasAgendaListTarget) return
    let n = 0
    this.agendaListTarget.querySelectorAll("[data-agenda-line] [data-agenda-num]").forEach(num => {
      num.textContent = `${++n}.`
    })
  }

  _agendaRequest(url, method, body) {
    if (!url) return Promise.reject("That isn't available here.")
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(url, {
      method,
      headers: { "X-CSRF-Token": token || "", "Accept": "application/json",
                 "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).then(r => r.json().then(d => (r.ok ? d : Promise.reject(d?.error || "That didn't work."))))
  }

  // Badge on the Agenda button — read off the list so it can't drift.
  _renderAgendaCount() {
    if (!this.hasAgendaCountTarget || !this.hasAgendaListTarget) return
    const n = this.agendaListTarget.querySelectorAll("li").length
    this.agendaCountTarget.textContent = String(n)
    this.agendaCountTarget.classList.toggle("hidden", n === 0)
  }

  _agendaFail(message) {
    if (!this.hasAgendaErrorTarget) return
    this.agendaErrorTarget.textContent = message
    this.agendaErrorTarget.classList.toggle("hidden", !message)
  }

  // ── Item 22: creator turns live transcription off/on mid-meeting ──
  toggleTranscription() {
    if (!this.creatorValue || !this.toggleUrlValue || this._txBusy) return
    this._txBusy = true
    if (this.hasTranscriptionPillTarget) this.transcriptionPillTarget.disabled = true

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.toggleUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token || "", "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(data => {
        this.pausedValue = !!data.paused
        this._renderTranscriptionPill()
      })
      .catch(() => { /* transient failure — leave the pill showing its last known state */ })
      .finally(() => {
        this._txBusy = false
        if (this.hasTranscriptionPillTarget) this.transcriptionPillTarget.disabled = false
      })
  }

  _renderTranscriptionPill() {
    if (!this.hasTranscriptionPillTarget) return
    const base = "shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] whitespace-nowrap transition-colors disabled:opacity-60 "
    this.transcriptionPillTarget.className = base + (this.pausedValue
      ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
      : "bg-green-50 text-green-700 hover:bg-green-100")
    // The recording dot follows (Larry item 29): red pulse on, gray off.
    if (this.hasTranscriptionDotTarget) {
      this.transcriptionDotTarget.className = "w-2 h-2 rounded-full shrink-0 " +
        (this.pausedValue ? "bg-gray-400" : "bg-red-500 animate-pulse")
    }
    this.transcriptionPillTarget.title = this.pausedValue
      ? "Transcription is off — tap to turn on"
      : "Transcription is on — tap to turn off"
    if (this.hasTranscriptionLabelTarget) {
      this.transcriptionLabelTarget.textContent = this.pausedValue ? "Transcription off" : "Transcribing"
    }
  }

  _join() {
    if (this._call) { try { this._call.destroy() } catch { /* noop */ } }

    this.frameTarget.innerHTML = ""
    const options = {
      url: this.dailyUrlValue,
      // The provider parks its own Leave at the far right of its bottom bar
      // and won't move it; Phelipe wants Leave right next to Share
      // (2026-09-22), so the provider's stays off and the view lays a
      // look-alike over the bar at that spot. One Leave, not two; ours came
      // out of the header (where Larry had it on 09-17).
      showLeaveButton: false,
      showFullscreenButton: true,
      iframeStyle: { position: "absolute", inset: "0", width: "100%", height: "100%", border: "0" },
      theme: {
        colors: {
          accent: this.accentValue,
          accentText: "#ffffff",
          baseText: "#ffffff",
          border: "#2c3542",
          background: "#232a35",
          backgroundAccent: "#2c3542",
          mainAreaBg: "#232a35",
          mainAreaBgAccent: "#4b5563",
          mainAreaText: "#ffffff",
          supportiveText: "#c8cdd6"
        }
      }
    }
    // Signed-in ConsLog users arrive named — the camera-check screen shows
    // their real name instead of asking them to type one...
    if (this.userNameValue) options.userName = this.userNameValue
    // ...and their ConsLog profile photo shows on their tile when the
    // camera is off (Daily Prebuilt reads userData.avatar).
    if (this.avatarValue) options.userData = { avatar: this.avatarValue }

    try {
      this._call = DailyIframe.createFrame(this.frameTarget, options)
    } catch (e) {
      return this._failed(e)
    }
    // "loaded" fires when Daily's UI (the camera/mic check screen) is up —
    // clear our spinner then, not at join, or the haircheck sits hidden.
    this._call.on("loaded", () => this._hideLoading())
    this._call.on("joined-meeting", () => this._hideLoading())
    this._call.on("left-meeting", () => this._showLeft())
    this._call.on("error", (e) => this._failed(e))
    this._call.on("app-message", (e) => { if (e?.data?.type === "agenda_reordered") this._refreshAgendaPanel() })
    // join() resolves after the user passes the camera-check and enters.
    this._call.join().catch((e) => this._failed(e))
  }

  // Our own Leave button (Larry 2026-09-15). The provider's leave control
  // lives inside the video frame, where it is small and easy to miss; this
  // one hangs up the same call and falls back to the "you left" panel.
  leaveCall() {
    if (!this._call) return this._showLeft()
    try { this._call.leave() } catch (_) { this._showLeft() }
  }

  _hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
  }

  // The Leave pill belongs to the call; once you have left, or the room
  // never opened, it goes with it.
  _hideControls() {
    this.element.querySelector("[data-meet-controls]")?.classList.add("hidden")
  }

  _showLeft() {
    this._hideLoading()
    this._hideControls()
    if (this._call) { try { this._call.destroy() } catch { /* noop */ } this._call = null }
    if (this.hasFrameTarget) this.frameTarget.innerHTML = ""
    if (this.hasLeftTarget) { this.leftTarget.classList.remove("hidden"); this.leftTarget.classList.add("flex") }
  }

  // Tell the truth about what went wrong: a room that's expired/ended gets
  // the calm closed-room message; anything else a retry hint with the
  // provider's reason so support isn't guessing.
  _failed(e) {
    this._hideLoading()
    this._hideControls()
    if (!this.hasFrameTarget) return

    const reason = (e && (e.errorMsg || e.message || e.error)) ? String(e.errorMsg || e.message || e.error) : ""
    const expired = /expired|does not exist|no longer available|not found/i.test(reason)
    const body = expired
      ? "This meeting room has closed. Meeting minutes are prepared automatically in ConsLog — check the meeting page or your email."
      : `The meeting room couldn't load${reason ? ` (${reason})` : ""}. Refresh this page to try again.`
    this.frameTarget.innerHTML =
      '<div class="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-gray-500"><div class="max-w-md">' +
      body + "</div></div>"
  }
}
